import { useChatStatus } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn } from '@newfront-insurance/core-ui/v2';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { MessageResponse, SourceBadge, Sources, SourcesContent, SourcesTrigger } from '@newfront-insurance/nova-core';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useCopyToClipboard } from 'react-use';

import { ChainOfThoughtBlock } from './chain-of-thought-block';
import { ChatContextUsage } from './chat-context-usage';
import { MarkdownWithCitations } from './markdown-with-citations';
import { GeneratedFilesList } from './tools/process-files-tool';
import { getClarificationPart, isClarificationPending, isClarificationToolPart } from '../../../lib/clarification';
import { containsSandboxLinks } from '../../../lib/preprocess-sandbox-links';
import { startsWithSkillCommand } from '../../../lib/skill-command-utils';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { sanitizeText } from '../../../lib/utils';
import { ThumbsDown } from '../../icons/thumbs-down';
import { ThumbsUp } from '../../icons/thumbs-up';
import { MessageAction, MessageActions } from '../../message-actions';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useChainOfThoughtSteps } from '../hooks/use-chain-of-thought-steps';
import { type DataPartRenderer, type ToolOutputRenderer } from '../lib/default-renderers';
import {
  buildSandboxFileMap,
  extractAllSourcesFromMessage,
  extractSourcesFromMessage,
  getSourcesArray,
  hasSources,
  type SourcesMap,
} from '../lib/sources';

const ADD_MESSAGE_REACTION = graphql(`
  mutation AddMessageReaction($messageUuid: String!, $reaction: MessageReaction) {
    updateMessageReaction(messageUuid: $messageUuid, reaction: $reaction) {
      uuid
      reaction
    }
  }
`);

interface PreviewMessageProps {
  message: UIMessageWithMetadata;
  isStreaming: boolean;
  isLast: boolean;
  onReaction: (reaction: MessageReaction | null) => void;
  /**
   * Sources accumulated from messages before the current one.
   * Used to resolve follow-up citations when source-url parts are omitted.
   */
  sourceFallbackMap?: SourcesMap;
  /**
   * Object mapping tool names to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * If provided, these renderers will be used instead of default rendering.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Optional renderer function for content to display inside the message bubble
   * (for user messages) before the text content. Receives the message as a parameter.
   */
  beforeContentRenderer?: (message: UIMessageWithMetadata) => ReactNode;
  /**
   * Optional className to apply to the wrapper around beforeContentRenderer output.
   * Useful for adjusting spacing or positioning of the before-content element.
   */
  beforeContentClassName?: string;
  /**
   * Optional className to apply to the message content container (the bubble).
   * Useful for adjusting padding or other styling of the message bubble.
   */
  messageContentClassName?: string;
  /**
   * Controls whether inline citations and sources panel are rendered.
   * When false, falls back to plain markdown rendering using Response component.
   * Defaults to true for backward compatibility.
   */
  enableSourcesRendering?: boolean;
}

/**
 * Renders text with the slash command handle highlighted in fire-400 color.
 * Extracts the handle from the beginning of the text (e.g., "/compare-policies").
 * Preserves whitespace formatting exactly as typed.
 */
function SlashCommandHighlightedText({ text }: { text: string }) {
  // Match slash command handle at the start: /word-characters
  const match = text.match(/^(\/[\w-]+)(.*)/s);

  if (!match) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }

  const [, handle, rest] = match;
  return (
    <span className="whitespace-pre-wrap">
      <span className="text-brand-400">{handle}</span>
      {rest}
    </span>
  );
}

/**
 * Renders the message text content with appropriate formatting based on message type.
 */
function MessageTextContent({
  message,
  sanitizedText,
  messageHasSources,
  hasShellFiles,
  sourcesMap,
  allSourcesMap,
  enableSourcesRendering,
  enableDataExplorerSources,
}: {
  message: UIMessageWithMetadata;
  sanitizedText: string;
  messageHasSources: boolean;
  hasShellFiles: boolean;
  sourcesMap: SourcesMap;
  allSourcesMap?: SourcesMap;
  enableSourcesRendering?: boolean;
  enableDataExplorerSources?: boolean;
}) {
  const hasSandboxLinks = containsSandboxLinks(sanitizedText);

  // Assistant messages with sources or shell files get citations/sandbox link rendering
  if (
    (messageHasSources || hasShellFiles || hasSandboxLinks) &&
    message.role === 'assistant' &&
    enableSourcesRendering !== false
  ) {
    return (
      <MarkdownWithCitations
        messageUuid={message.id}
        sourcesMap={sourcesMap}
        allSourcesMap={allSourcesMap}
        enableDataExplorerSources={enableDataExplorerSources}
        className="size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
      >
        {sanitizedText}
      </MarkdownWithCitations>
    );
  }

  // User messages with slash-prefixed Nova commands get highlighted handles
  if (message.role === 'user' && startsWithSkillCommand(sanitizedText)) {
    return <SlashCommandHighlightedText text={sanitizedText} />;
  }

  // Default: render as markdown
  return <MessageResponse messageUuid={message.id}>{sanitizedText}</MessageResponse>;
}

function ChatFileListItem({ file }: { file: { uuid: string; filename: string } }) {
  const { preview, isDownloading } = useDownloadFile({});

  const handleClick = () => {
    void preview(file.uuid, file.filename);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        { 'pointer-events-none opacity-50': isDownloading },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
    </div>
  );
}

export function PreviewMessage({
  message,
  isStreaming,
  isLast,
  onReaction,
  sourceFallbackMap,
  toolRenderers,
  dataPartRenderers,
  beforeContentRenderer,
  beforeContentClassName,
  messageContentClassName,
  enableSourcesRendering,
}: PreviewMessageProps) {
  const chatStatus = useChatStatus();
  const notifications = useNotifications();
  const [updateMessageReaction] = useMutation(ADD_MESSAGE_REACTION, {});
  const overlayContext = useChatOverlayContextSafe();
  const isPublicMode = overlayContext?.isPublicMode ?? false;
  const isReadOnly = overlayContext?.isReadOnly ?? false;
  const isReasoningEnabled = useFeatureFlag('nova-display-reasoning', false);
  const isDataExplorerSourcesEnabled = useFeatureFlag('nova-data-explorer-sources', true);
  const clarificationPart = getClarificationPart(message);
  const messageHasBlockingClarification =
    isClarificationPending(message) ||
    (isLast && clarificationPart?.state === 'output-available' && chatStatus !== 'ready');

  // Extract sources from message parts
  const messageHasSources = hasSources(message, sourceFallbackMap);
  const sourcesMap = messageHasSources ? extractSourcesFromMessage(message, sourceFallbackMap) : new Map();
  const sourcesArray = sourcesMap.size > 0 ? getSourcesArray(sourcesMap) : [];

  // All sources (unfiltered) — used by CoT and sandbox link resolution.
  // Recompute directly from current message parts so streaming updates aren't hidden behind stale memoization.
  const allSourcesMap = extractAllSourcesFromMessage(message);

  // Shell-generated files for download rendering (from newfrontShell sources)
  const shellGeneratedFiles = Array.from(buildSandboxFileMap(allSourcesMap).values()).filter(
    (file) => file.kind === 'file',
  );
  const hasShellFiles = shellGeneratedFiles.length > 0;

  // Extract chain-of-thought steps from message parts (reasoning + data-progress)
  const messageIsWorking = message.role === 'assistant' && isLast && ['submitted', 'streaming'].includes(chatStatus);
  const { chainOfThoughtMeta, isWorking, stepProgressMap } = useChainOfThoughtSteps(
    message,
    messageIsWorking,
    isReasoningEnabled,
  );

  const handleReaction = (newReaction: MessageReaction) => {
    if (isReadOnly) {
      return;
    }

    // Toggle reaction - if same reaction clicked, remove it
    const reaction = message.metadata?.reaction === newReaction ? null : newReaction;
    if (!isPublicMode) {
      updateMessageReaction({
        variables: {
          messageUuid: message.id,
          reaction,
        },
        optimisticResponse: {
          updateMessageReaction: {
            uuid: message.id,
            reaction,
          },
        },
      });
    }

    onReaction(reaction);

    if (reaction) {
      notifications.add({
        type: 'success',
        duration: 2000,
        title: 'Thank you for your feedback!',
      });
    }
  };

  const [_, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  const handleCopyToClipboard = useCallback(() => {
    const lastMessagePart = message.parts?.[message.parts.length - 1];

    if (!lastMessagePart) {
      return;
    }

    copyToClipboard(lastMessagePart.type === 'text' ? lastMessagePart.text : '');
    setIsCopied(true);
  }, [message.parts, copyToClipboard]);

  // Skip rendering the wrapper for assistant messages with no visible content
  // (e.g. streaming just started, only step-start parts exist).
  // This prevents an empty div from occupying space above the thinking dots.
  // NOTE: placed after all hooks to respect React's rules of hooks.
  const hasRenderableContent =
    message.role === 'user' ||
    chainOfThoughtMeta.hasTextPart ||
    (isReasoningEnabled && chainOfThoughtMeta.steps.length > 0) ||
    (message.metadata?.files?.length ?? 0) > 0 ||
    message.parts?.some(
      (p) =>
        (p.type.startsWith('tool-') && toolRenderers?.[p.type.replace('tool-', '')]) ||
        (p.type.startsWith('data-') && dataPartRenderers?.[p.type.replace('data-', '')]),
    );

  if (!hasRenderableContent) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className="group/message mx-auto w-full max-w-[800px] sm:px-8"
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
        key={message.id}
      >
        <div
          className={cn(
            'flex w-full items-start gap-4 group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-full',
          )}
        >
          <div className="flex w-full flex-col gap-2">
            {message.metadata?.files && message.metadata.files.length > 0 && (
              <div className="flex flex-row flex-wrap gap-2">
                {message.metadata?.files?.map((file) => (
                  <ChatFileListItem key={file.uuid} file={file} />
                ))}
              </div>
            )}
            <div className={cn({ 'ml-auto max-w-full': message.role === 'user' })}>
              {isReasoningEnabled && chainOfThoughtMeta.steps.length > 0 && (
                <ChainOfThoughtBlock
                  steps={chainOfThoughtMeta.steps}
                  isWorking={isWorking}
                  stepProgressMap={stepProgressMap}
                  sourcesMap={isDataExplorerSourcesEnabled ? allSourcesMap : undefined}
                />
              )}
              {message.parts?.map((part, index) => {
                const { type } = part;
                const key = `message-${message.id}-part-${index}`;

                if (type.startsWith('tool-')) {
                  const toolName = type.replace('tool-', '');

                  // When reasoning is enabled, tool pending states are already
                  // displayed inside the ChainOfThought block — skip them here.
                  if (
                    isReasoningEnabled &&
                    toolName !== 'clarification' &&
                    'state' in part &&
                    part.state !== 'output-available'
                  ) {
                    return null;
                  }

                  if (toolRenderers?.[toolName]) {
                    const rendered = toolRenderers[toolName](part, message.id);
                    if (rendered) {
                      const isResolvedClarification =
                        isClarificationToolPart(part) && part.state === 'output-available';

                      return (
                        <div key={key} className={cn({ 'flex w-full justify-end': isResolvedClarification })}>
                          {rendered}
                        </div>
                      );
                    }
                  }
                  return null;
                }

                if (type.startsWith('data-')) {
                  const dataPartType = type.replace('data-', '');
                  const DataPartComponent = dataPartRenderers?.[dataPartType];
                  if (DataPartComponent) {
                    return <DataPartComponent key={key} />;
                  }
                }

                if (type === 'reasoning') {
                  // Reasoning parts are rendered inside the ChainOfThought block above
                  return null;
                }

                if (type === 'data-progress' && isReasoningEnabled) {
                  // Progress parts are rendered inside ChainOfThought when reasoning is enabled
                  return null;
                }

                if (type === 'text') {
                  const sanitizedText = sanitizeText(part.text);

                  return (
                    <div key={key} className="flex flex-row items-start gap-2">
                      <div
                        data-testid="message-content"
                        className={cn(
                          'flex max-w-full flex-col gap-4 rounded-[20px] py-2',
                          {
                            'bg-muted rounded-br-[0px]': message.role === 'user',
                            'rounded-bl-[0px]': message.role === 'assistant',
                            'px-5': message.role === 'user',
                          },
                          messageContentClassName,
                        )}
                      >
                        {beforeContentRenderer && message.role === 'user' && (
                          <div className={beforeContentClassName}>{beforeContentRenderer(message)}</div>
                        )}
                        <MessageTextContent
                          message={message}
                          sanitizedText={sanitizedText}
                          messageHasSources={messageHasSources}
                          hasShellFiles={hasShellFiles}
                          sourcesMap={sourcesMap}
                          allSourcesMap={allSourcesMap}
                          enableSourcesRendering={enableSourcesRendering}
                          enableDataExplorerSources={isDataExplorerSourcesEnabled}
                        />
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {message.role === 'assistant' && hasShellFiles && (
              <GeneratedFilesList
                files={shellGeneratedFiles.map((file) => ({
                  uuid: file.fileUuid,
                  filename: file.filename,
                  isPending: file.isPending,
                }))}
              />
            )}

            {/* Show sources panel for assistant messages with sources */}
            {message.role === 'assistant' && sourcesArray.length > 0 && enableSourcesRendering !== false && (
              <Sources>
                <SourcesTrigger count={sourcesArray.length} />
                <SourcesContent>
                  <div className="flex flex-wrap gap-2">
                    {sourcesArray.map((source, index) => (
                      <SourceBadge key={source.sourceId} source={source} index={index} />
                    ))}
                  </div>
                </SourcesContent>
              </Sources>
            )}

            {/* Show reactions only for assistant messages */}
            {message.role === 'assistant' && !(isStreaming && isLast) && !messageHasBlockingClarification && (
              <div className="transition-opacity">
                <MessageActions>
                  <MessageAction
                    tooltip={isCopied ? 'Copied!' : 'Copy to clipboard'}
                    label={isCopied ? 'Copied!' : 'Copy to clipboard'}
                    onClick={handleCopyToClipboard}
                  >
                    {isCopied ? (
                      <Check aria-label="Copied" color="currentColor" size={18} />
                    ) : (
                      <ClipboardList aria-label="Copy to clipboard" color="currentColor" size={18} />
                    )}
                  </MessageAction>
                  {!isReadOnly && (message.metadata?.reaction === 'THUMBS_UP' || !message.metadata?.reaction) && (
                    <MessageAction
                      tooltip="Good response"
                      label="Thumbs up"
                      isActive={message.metadata?.reaction === 'THUMBS_UP'}
                      onClick={() => handleReaction('THUMBS_UP')}
                    >
                      <ThumbsUp aria-label="Thumbs up" active={message.metadata?.reaction === 'THUMBS_UP'} />
                    </MessageAction>
                  )}
                  {!isReadOnly && (message.metadata?.reaction === 'THUMBS_DOWN' || !message.metadata?.reaction) && (
                    <MessageAction
                      tooltip="Bad response"
                      label="Thumbs down"
                      isActive={message.metadata?.reaction === 'THUMBS_DOWN'}
                      aria-label="Thumbs down"
                      onClick={() => handleReaction('THUMBS_DOWN')}
                    >
                      <ThumbsDown aria-label="Thumbs down" active={message.metadata?.reaction === 'THUMBS_DOWN'} />
                    </MessageAction>
                  )}
                  <ChatContextUsage messageId={message.id} />
                </MessageActions>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
